import React, { useState } from 'react';
import { LuSearch, LuX } from 'react-icons/lu';

import { HoldingsReport } from '@ndvr/reporting-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/utils/cn';
import { pluralize } from '@/utils/string-utils';

import { getBondRows, getCashRows, getEquityRows, getHoldingsTotal, HoldingRow } from '../../holdings';
import { formatFullCurrency, formatFullPercent } from '../../reporting/format';
import {
    gainCell,
    HEAD_CLASS,
    HEAD_ROW_CLASS,
    MV_CELL,
    NUMERIC_CELL,
    RIGHT_HEAD_CLASS,
    TABLE_CLASS,
    TABLE_FOOTER_CLASS,
} from '../../reporting/table-styles';
import { HoldingTableRow } from './HoldingCells';

interface HoldingsBreakdownTableProps {
    report?: HoldingsReport | null;
    isLoading?: boolean;
    // "As of EOD …" for the footers; built once by HoldingsSection.
    asOf: string;
}

// For the empty-state cells that span the whole table.
const COLUMN_COUNT = 9;

// Each tab lists its largest positions only; "View all" opens every position.
const TOP_ROWS = 10;

// Column header shared by all three tabs — the tables differ only in their rows.
const HoldingsColumnHeader = () => (
    <TableHeader>
        <TableRow className={HEAD_ROW_CLASS}>
            <TableHead className={HEAD_CLASS}>Symbol</TableHead>
            <TableHead className={HEAD_CLASS}>Security</TableHead>
            <TableHead className={HEAD_CLASS}>Asset Class</TableHead>
            <TableHead className={RIGHT_HEAD_CLASS}>Weight</TableHead>
            <TableHead className={RIGHT_HEAD_CLASS}>Price</TableHead>
            <TableHead className={RIGHT_HEAD_CLASS}>Qty</TableHead>
            <TableHead className={RIGHT_HEAD_CLASS}>Market Value</TableHead>
            <TableHead className={RIGHT_HEAD_CLASS}>Unrealized G/L</TableHead>
            <TableHead className={RIGHT_HEAD_CLASS}>Return</TableHead>
        </TableRow>
    </TableHeader>
);

// Totals of every row in the tab, not just the visible top rows. Same tinted total row as
// the Performance by Portfolio table.
const HoldingsTotalRow = ({ label, rows }: { label: string; rows: HoldingRow[] }) => {
    const total = getHoldingsTotal(rows);
    return (
        <TableRow className="bg-cocoa-100/40 hover:bg-cocoa-100/40">
            <TableCell colSpan={3} className="text-cocoa-1000 text-sm font-semibold">
                Total {label.toLowerCase()} · {pluralize(rows.length, 'position')}
            </TableCell>
            <TableCell className={cn(NUMERIC_CELL, 'text-cocoa-1000 font-semibold')}>
                {formatFullPercent(total.weight)}
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell className={MV_CELL}>{formatFullCurrency(total.marketValue)}</TableCell>
            <TableCell className={cn(gainCell(total.unrealizedGainLoss), 'font-semibold')}>
                {formatFullCurrency(total.unrealizedGainLoss)}
            </TableCell>
            <TableCell className={cn(gainCell(total.returnPercent), 'font-semibold')}>
                {formatFullPercent(total.returnPercent)}
            </TableCell>
        </TableRow>
    );
};

interface HoldingsTableProps {
    rows: HoldingRow[];
    // Shown in place of the rows when there are none, e.g. "No equity holdings for this scope."
    emptyText: string;
    // Rendered after the rows, e.g. a tab's total row.
    totalRow?: React.ReactNode;
    footerLeft: string;
    asOf: string;
    // Classes for the wrapper around the table (not the footer), e.g. the dialog's scroll area.
    className?: string;
}

// The holdings table and its footer, shared by the tabs and the "View all" dialog. All rows
// share HoldingRow — the row model carries the per-class differences (secondaryText, null
// price/quantity).
const HoldingsTable = ({ rows, emptyText, totalRow, footerLeft, asOf, className }: HoldingsTableProps) => (
    <>
        <div className={className}>
            <Table className={TABLE_CLASS}>
                <HoldingsColumnHeader />
                <TableBody>
                    {rows.length > 0 ? (
                        // Keyed by position: the report has no per-entry id and can list the same security
                        // more than once with identical fields (AMZN did), and the rows hold no state.
                        rows.map((row, index) => (
                            <HoldingTableRow key={`${row.assetClass}-${row.symbol}-${row.name}-${index}`} row={row} />
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={COLUMN_COUNT} className="text-cocoa-400 py-6 text-center text-sm">
                                {emptyText}
                            </TableCell>
                        </TableRow>
                    )}
                    {totalRow}
                </TableBody>
            </Table>
        </div>
        <div className={cn(TABLE_FOOTER_CLASS, 'flex items-center justify-between')}>
            <span>{footerLeft}</span>
            <span>{asOf}</span>
        </div>
    </>
);

interface HoldingsTabPanelProps {
    label: string;
    rows: HoldingRow[];
    emptyText: string;
    asOf: string;
}

// One tab: its TOP_ROWS largest positions (rows arrive sorted by market value) and a total
// row over all of them.
const HoldingsTabPanel = ({ label, rows, emptyText, asOf }: HoldingsTabPanelProps) => {
    const visibleRows = rows.slice(0, TOP_ROWS);
    return (
        <HoldingsTable
            rows={visibleRows}
            emptyText={emptyText}
            totalRow={rows.length > 0 ? <HoldingsTotalRow label={label} rows={rows} /> : null}
            footerLeft={rows.length > 0 ? `Positions 1 to ${visibleRows.length} of ${rows.length}` : '0 positions'}
            asOf={asOf}
        />
    );
};

// The "View all" dialog's contents. Its own component so the search state lives inside the
// dialog: Radix unmounts the content on close, so every open starts with an empty search.
const AllHoldingsDialogBody = ({ rows, asOf }: { rows: HoldingRow[]; asOf: string }) => {
    const [search, setSearch] = useState('');
    const query = search.trim().toLowerCase();
    const matchingRows = query
        ? rows.filter((row) => row.symbol.toLowerCase().includes(query) || row.name.toLowerCase().includes(query))
        : rows;

    return (
        <>
            <DialogHeader className="flex-row flex-wrap items-center gap-3 space-y-0 border-b px-6 py-5">
                <DialogTitle className="text-cocoa-1000 flex-1 text-xl">All holdings</DialogTitle>
                <InputGroup className="border-cocoa-200 h-9 w-70 rounded-lg bg-white">
                    <InputGroupAddon align="inline-start">
                        <LuSearch className="text-cocoa-400 h-4 w-4" />
                    </InputGroupAddon>
                    <InputGroupInput
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search symbol and security"
                        aria-label="Search symbol and security"
                        className="placeholder:text-cocoa-400"
                    />
                </InputGroup>
                {/* Design's close button: 36px square like the search field, 8px radius, tint on hover. */}
                <DialogClose asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-cocoa-700 hover:bg-cocoa-100 h-9 w-9 rounded-lg"
                    >
                        <LuX className="h-5 w-5" />
                        <span className="sr-only">Close</span>
                    </Button>
                </DialogClose>
            </DialogHeader>
            {/* min-h-0 lets the table's wrapper shrink below its content so it scrolls instead of
                growing the fixed-height dialog. */}
            <HoldingsTable
                rows={matchingRows}
                emptyText={`No positions match “${search.trim()}”.`}
                footerLeft={
                    query
                        ? `Showing ${matchingRows.length} of ${pluralize(rows.length, 'position')}`
                        : pluralize(rows.length, 'position')
                }
                asOf={asOf}
                className="min-h-0 flex-1 overflow-auto"
            />
        </>
    );
};

export const HoldingsBreakdownTable = ({ report, isLoading, asOf }: HoldingsBreakdownTableProps) => {
    const [isAllOpen, setIsAllOpen] = useState(false);

    if (isLoading) {
        return <div className="bg-cocoa-100 h-64 w-full animate-pulse rounded-2xl" />;
    }

    const tabs = [
        {
            value: 'equities',
            label: 'Equities',
            rows: report ? getEquityRows(report) : [],
            emptyText: 'No equity holdings for this scope.',
        },
        {
            value: 'bonds',
            label: 'Bonds',
            rows: report ? getBondRows(report) : [],
            emptyText: 'No bond holdings for this scope.',
        },
        {
            value: 'cash',
            label: 'Cash & Equivalents',
            rows: report ? getCashRows(report) : [],
            emptyText: 'No cash & equivalents for this scope.',
        },
    ];
    // Every row across the tabs, grouped by tab and largest first within each, so the
    // "View all" count and dialog match the tab counts.
    const allRows = tabs.flatMap((tab) => tab.rows);

    return (
        <Card className="border-border overflow-hidden rounded-2xl border bg-white">
            <Tabs defaultValue="equities">
                {/* The button sits beside (not inside) TabsList so it stays out of the tabs' arrow-key
                    navigation; the wrapper carries the strip's bottom rule instead. */}
                <div className="flex items-center gap-4 border-b border-[#E5E4E1] pr-6">
                    <TabsList className="flex-1 justify-start border-b-0">
                        {tabs.map((tab) => (
                            <TabsTrigger key={tab.value} value={tab.value}>
                                {tab.label} <span className="text-cocoa-500 ml-1.5">{tab.rows.length}</span>
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {/* The design system's "link" button: transparent, teal text (seafoam-600), a light
                        teal tint on hover (seafoam-0), 36px tall, 14px semibold, 8px radius. */}
                    {allRows.length > 0 ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsAllOpen(true)}
                            className="text-seafoam-600 hover:bg-seafoam-0 rounded-lg px-3.5 font-semibold"
                        >
                            View all {allRows.length} positions
                        </Button>
                    ) : null}
                </div>
                {tabs.map((tab) => (
                    <TabsContent key={tab.value} value={tab.value} className="pt-0">
                        <HoldingsTabPanel label={tab.label} rows={tab.rows} emptyText={tab.emptyText} asOf={asOf} />
                    </TabsContent>
                ))}
            </Tabs>

            <Dialog open={isAllOpen} onOpenChange={setIsAllOpen}>
                {/* Fixed height (not max-h) so filtering the list doesn't shrink the dialog. */}
                <DialogContent showCloseButton={false} className="flex h-[85vh] flex-col gap-0 p-0 sm:max-w-6xl">
                    <AllHoldingsDialogBody rows={allRows} asOf={asOf} />
                </DialogContent>
            </Dialog>
        </Card>
    );
};
